<script>
import {
  BLOCO_PLANO_DA_PREPARACAO,
  ESTADO_DO_PASSO_DO_PLANO,
} from '@modules/aawz-intelligence-v2/constants/TextChat'

/**
 * O Progress da preparação de reunião e demais atalhos que publicam passos de
 * negócio — spec-001 `project-home-ia-skills/preparacao-reuniao`, [T016],
 * D8.2, US-2.
 *
 * Componente BURRO (mesma disciplina de `next-steps.vue`): recebe `itens`
 * (`plano.itens`, já resolvido pelo dedupe de `apiChat.js::_onPlano`) e só
 * desenha — nenhuma regra de negócio, nenhuma leitura de store.
 *
 * `estado` de cada item ∈ `pendente | em_andamento | concluido | falhou`
 * (`ESTADO_DO_PASSO_DO_PLANO`, espelho de `core/plano_vivo.py`). Passo
 * concluído risca o texto e marca com check; o corrente é destacado; um
 * passo que falhou mostra o motivo NAQUELE passo e os demais SEGUEM — falha
 * parcial não derruba a entrega (`spec.md` US-2).
 */
export default {
  name: 'IntelligenceV2PlanoProgress',
  props: {
    // `plano.itens` — `[{id, texto, estado, erro?}]`. `erro` é opcional: o
    // texto que aparece sob o passo quando `estado === 'falhou'`.
    itens: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return { ESTADO: ESTADO_DO_PASSO_DO_PLANO }
  },
  computed: {
    titulo() {
      return BLOCO_PLANO_DA_PREPARACAO.titulo
    },
    // Pré-computado aqui, e não no template: o `v-for` roda a cada tick do
    // streaming, e chamar função no template não tem cache (regra "Sem
    // Chamada de Função no Template").
    passos() {
      return (this.itens || []).map((item, indice) => {
        const estado = (item && item.estado) || this.ESTADO.PENDENTE
        return {
          chave: (item && item.id) || 'plano-passo-' + indice,
          ordem: indice + 1,
          texto: (item && item.texto) || '',
          estado: estado,
          erro: (item && item.erro) || '',
          concluido: estado === this.ESTADO.CONCLUIDO,
          emAndamento: estado === this.ESTADO.EM_ANDAMENTO,
          falhou: estado === this.ESTADO.FALHOU,
        }
      })
    },
    mostraBloco() {
      return this.passos.length > 0
    },
  },
}
</script>

<template>
  <div v-if="mostraBloco" class="plano-progress ia-block-card" data-teste="plano-progress">
    <div class="ia-block-card__cabecalho ia-block-card__cabecalho--fita">
      <icon-svg name="task-square" :size="14" color="var(--primary)" class="ia-block-card__icone" />
      <span class="ia-block-card__titulo">{{ titulo }}</span>
    </div>

    <ol class="plano-progress__lista">
      <li
        v-for="passo in passos"
        :key="passo.chave"
        class="plano-progress__item"
        :class="{
          'plano-progress__item--concluido': passo.concluido,
          'plano-progress__item--atual': passo.emAndamento,
          'plano-progress__item--falhou': passo.falhou,
        }"
        :data-teste="'plano-progress-passo-' + passo.estado"
      >
        <span class="plano-progress__marcador">
          <icon-svg
            v-if="passo.concluido"
            name="check-circle"
            :size="18"
            color="var(--success-text)"
          />
          <icon-svg
            v-else-if="passo.falhou"
            name="error-circle"
            :size="18"
            color="var(--danger-text)"
          />
          <span v-else class="plano-progress__ordem ia-texto-selo">{{ passo.ordem }}</span>
        </span>
        <span class="plano-progress__corpo">
          <span class="plano-progress__texto ia-texto-auxiliar">{{ passo.texto }}</span>
          <span v-if="passo.falhou && passo.erro" class="plano-progress__erro ia-texto-auxiliar">{{
            passo.erro
          }}</span>
        </span>
      </li>
    </ol>
  </div>
</template>

<style lang="sass" scoped>
// Card, faixa e título vêm de `.ia-block-card*` (partial de módulo) — mesmo
// precedente de `next-steps.vue`.

// `display: block` reafirmado: a regra global `.content-nav ul` impõe
// `display: flex`, e o chat vive dentro dela — sem isto os passos viram
// colunas lado a lado.
.plano-progress__lista
  display: block
  margin: 0
  padding: 0
  list-style: none

.plano-progress__item + .plano-progress__item
  border-top: 1px solid var(--border-hairline)

.plano-progress__item
  display: flex
  align-items: flex-start
  gap: 10px
  padding: 10px 14px

.plano-progress__marcador
  display: inline-flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  width: 20px
  height: 20px
  margin-top: 1px

.plano-progress__ordem
  color: var(--neutral-700)
  display: inline-flex
  align-items: center
  justify-content: center
  width: 20px
  height: 20px
  border-radius: 50%
  background: var(--neutral-100)

.plano-progress__corpo
  display: flex
  flex-direction: column
  gap: 3px
  flex: 1 1 auto
  min-width: 0

// O passo CORRENTE é o destaque desta lista — cor de marca, sem shimmer:
// quem já anima na bolha é a `raciocinio-linha` ("um indicador por vez").
.plano-progress__item--atual .plano-progress__texto
  color: var(--primary)
  font-weight: 600

.plano-progress__item--atual .plano-progress__ordem
  background: var(--primary)
  color: var(--white)

// Concluído risca o texto e esmaece — o passo já aconteceu.
.plano-progress__item--concluido .plano-progress__texto
  color: var(--neutral-600)
  text-decoration: line-through

.plano-progress__item--falhou .plano-progress__texto
  color: var(--danger-text)
  font-weight: 600

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.plano-progress__texto
  color: var(--neutral-700)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.plano-progress__erro
  color: var(--danger-text)
</style>
